import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, RefreshCw, X, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

// "Aus Screenshots analysieren" (Re-Analyze) — moved from the old Settings
// "Inspirationen" tab. Self-contained, prop { getAuthHeader }. Endpoints unchanged:
//   GET  /settings/presence-profile        (list uploaded presence files)
//   POST /settings/upload-presence-file    (upload)
//   DELETE /settings/presence-file/{id}    (delete)
//   POST /settings/re-analyze-platforms    (re-run brand analysis)
// Does NOT touch the image pipeline. Dark theme.

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const UploadedFilesList = ({ files, onDelete }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[#A0AEC0] text-sm hover:text-white transition-colors mb-2">
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        {t.settings.inspirations.showPreviousUploads.replace('{n}', files.length)}
      </button>
      {expanded && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
          {files.map((file) => (
            <div key={file.id} className="relative group">
              <img src={file.url} alt={file.original_name} className="w-full h-20 object-cover rounded-lg" />
              <button onClick={() => onDelete(file.id)} className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BrandDnaReanalyze = ({ getAuthHeader }) => {
  const { t } = useLanguage();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [analyzingPresence, setAnalyzingPresence] = useState(false);

  const loadPresenceFiles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/presence-profile`, { headers: getAuthHeader() });
      setUploadedFiles(r.data.files || []);
    } catch { }
  }, [getAuthHeader]);

  useEffect(() => { loadPresenceFiles(); }, [loadPresenceFiles]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingFile(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const r = await axios.post(`${API}/settings/upload-presence-file`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
        setUploadedFiles(prev => [...prev, r.data]);
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/settings/presence-file/${fileId}`, { headers: getAuthHeader() });
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleAnalyzePresence = async () => {
    if (uploadedFiles.length === 0) { toast.error('Bitte lade zuerst Screenshots hoch'); return; }
    setAnalyzingPresence(true);
    try {
      // Faithful to the old tab: send existing text inspirations as context.
      let inspirationPosts = [];
      try {
        const ir = await axios.get(`${API}/brand/inspirations`, { headers: getAuthHeader() });
        inspirationPosts = (ir.data.inspirations || []).filter(i => i.text?.trim()).map(i => ({ text: i.text, image_b64: null }));
      } catch { }
      await axios.post(`${API}/settings/re-analyze-platforms`, {
        language: 'de',
        platforms: { general: { posts: inspirationPosts.length > 0 ? inspirationPosts.slice(0, 10) : [{ text: 'Markenanalyse', image_b64: null }] } }
      }, { headers: getAuthHeader(), timeout: 180000 });
      toast.success('Brand-Analyse abgeschlossen');
    } catch (err) { toast.error(err.response?.data?.detail || 'Analyse fehlgeschlagen'); }
    finally { setAnalyzingPresence(false); }
  };

  return (
    <Card className="bg-[#1A2B42] border-transparent">
      <CardHeader>
        <CardTitle className="text-white text-base flex items-center gap-2"><Upload className="w-4 h-4 text-[#00D9C8]" />{t.settings.inspirations.screenshotsCard}</CardTitle>
        <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.inspirations.screenshotsDesc}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-[#5A6C7D] rounded-lg p-4 text-center hover:border-[#00D9C8] transition-colors">
          <input type="file" multiple accept="image/*" onChange={handleFileUpload} className="hidden" id="presence-upload-hybrid" disabled={uploadingFile} />
          <label htmlFor="presence-upload-hybrid" className="cursor-pointer">
            {uploadingFile ? <Loader2 className="w-6 h-6 mx-auto mb-2 text-[#00D9C8] animate-spin" /> : <Upload className="w-6 h-6 mx-auto mb-2 text-[#5A6C7D]" />}
            <p className="text-sm text-white">{uploadingFile ? t.settings.inspirations.uploading : t.settings.inspirations.clickToUpload}</p>
          </label>
        </div>
        {uploadedFiles.length > 0 && (
          <UploadedFilesList files={uploadedFiles} onDelete={handleDeleteFile} />
        )}
        {uploadedFiles.length > 0 && (
          <Button onClick={handleAnalyzePresence} disabled={analyzingPresence} className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold">
            {analyzingPresence ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.settings.inspirations.analyzing}</> : <><RefreshCw className="w-4 h-4 mr-2" />{t.settings.inspirations.analyzeFromScreenshots}</>}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default BrandDnaReanalyze;
